// ---------------------------------------------------------------------------
// Taskbar badge write fence
//
// The installed app's taskbar number has several producers: the open page
// (every fresh /workitems/mine read), the push handler (a count computed on
// the server when the work arrived, delivered later at-least-once), and the
// page's own repaint. Written as absolute values with no ordering, they race:
// a push delivered late, or a page repaint of an older read, can land after a
// newer value and the number sticks wrong until something else writes.
//
// The fix is the usual one for a last-writer-wins value with several
// writers - a fencing token:
//
//   - Every write carries the SERVER observation instant (`asOfMs`), stamped
//     when the count was read. Client clocks are never compared.
//   - The writer keeps the instant of the last value it applied, persisted
//     (so a worker restart cannot forget it) and compared atomically with
//     the write. A write whose instant is older than the fence is dropped.
//   - Writes are serialized, so the compare, the paint, and the persist are
//     one step even when two observations arrive together.
//
// The service worker is the only caller of the surface, so the paint order
// matches the fence order. Pages never paint directly; they post observations
// to the worker (see appBadge.ts).
// ---------------------------------------------------------------------------

/** One server observation of the owed-work count. */
export interface BadgeObservation {
    readonly count: number;
    /** Server instant the count was read at, epoch milliseconds. */
    readonly asOfMs: number;
    /**
     * Re-paint even when the value is unchanged. Page-originated writes set
     * this (see appBadge.ts for why); push-originated writes do not.
     */
    readonly repaint: boolean;
}

/** The persisted fence: the last applied value and the instant it was observed at. */
export interface BadgeFence {
    readonly count: number;
    readonly asOfMs: number;
}

export interface BadgeFenceStore {
    read(): Promise<BadgeFence | undefined>;
    write(fence: BadgeFence): Promise<void>;
}

export interface BadgeSurface {
    set(count: number): Promise<void>;
    clear(): Promise<void>;
}

export type BadgeApplyOutcome = "applied" | "stale";

export interface BadgeWriter {
    apply(observation: BadgeObservation): Promise<BadgeApplyOutcome>;
}

export interface CreateBadgeWriterOptions {
    readonly store: BadgeFenceStore;
    readonly surface: BadgeSurface;
}

/**
 * Builds the single serialized writer. An observation older than the fence is
 * reported as `stale` and changes nothing. An equal instant is applied: the
 * same server read delivered twice is idempotent, and the instant has
 * millisecond resolution, so refusing ties could drop a genuinely newer read.
 *
 * The fence advances only after the paint succeeds. A failed paint therefore
 * leaves the fence where it was, and a later observation can still apply.
 */
export function createBadgeWriter({ store, surface }: CreateBadgeWriterOptions): BadgeWriter {
    const applyOne = async (observation: BadgeObservation): Promise<BadgeApplyOutcome> => {
        const fence = await store.read();
        if (fence !== undefined && observation.asOfMs < fence.asOfMs) {
            return "stale";
        }
        if (observation.count <= 0) {
            await surface.clear();
        } else {
            if (observation.repaint) {
                // Clear first so Windows redraws the overlay even if it
                // coalesces a set onto the value it already shows.
                await surface.clear();
            }
            await surface.set(observation.count);
        }
        await store.write({ count: observation.count, asOfMs: observation.asOfMs });
        return "applied";
    };

    let tail: Promise<unknown> = Promise.resolve();
    return {
        apply(observation) {
            const run = tail.then(() => applyOne(observation));
            tail = run.catch(() => undefined);
            return run;
        },
    };
}

/** The Badging API surface, narrowed structurally so the adapter compiles on any DOM lib version. */
export interface BadgingNavigator {
    setAppBadge?(contents?: number): Promise<void>;
    clearAppBadge?(): Promise<void>;
}

/**
 * Adapter over `navigator.setAppBadge` / `navigator.clearAppBadge`. Absent API
 * (a plain browser tab, or a browser without the Badging API) resolves as a
 * no-op: the badge is cosmetic and must never fail the write path.
 */
export function navigatorBadgeSurface(nav: BadgingNavigator): BadgeSurface {
    return {
        async set(count) {
            await nav.setAppBadge?.(count);
        },
        async clear() {
            await nav.clearAppBadge?.();
        },
    };
}

// Server timestamps carry 100 ns ticks (seven fractional digits). Trimming to
// milliseconds before parsing avoids depending on how each engine handles
// sub-millisecond ISO input. Two reads inside the same millisecond tie and are
// both applied (see createBadgeWriter); the next re-read settles it.
const FRACTION_BEYOND_MILLIS = /(\.\d{3})\d+/;

/** Parses a server ISO instant to epoch milliseconds, or `undefined` when unparseable. */
export function toObservationMillis(instant: string): number | undefined {
    const millis = Date.parse(instant.replace(FRACTION_BEYOND_MILLIS, "$1"));
    return Number.isFinite(millis) ? millis : undefined;
}
